/**
 * Station auth — token storage + login/whoami/touch helpers.
 *
 * Session token lives in localStorage under STORAGE_KEY. Tablets paste the
 * token in via /set-station-token; humans get one from the OTP flow.
 */

import type {
  RequestCodeResponse,
  SessionUser,
  VerifyCodeResponse,
} from "../types/auth";
import { ApiError, callFn } from "./api";

const STORAGE_KEY = "station.session_token";
const USER_CACHE_KEY = "station.cached_user";

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

export function logout(): void {
  setStoredToken(null);
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch {}

  // Garbage collect any user-prefixed Zustand keys to avoid shared tablet bleeding
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("sentinel_os_state_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore localstorage quota/access errors */
  }
}

/** Step 1 of the OTP flow — sends an SMS code. */
export function requestCode(emailOrPhone: string): Promise<RequestCodeResponse> {
  return callFn<RequestCodeResponse>("bar-request-code", {
    email_or_phone: emailOrPhone,
  });
}

/** Step 2 of the OTP flow — exchanges the code for a session token. */
export async function verifyCode(
  emailOrPhone: string,
  code: string
): Promise<VerifyCodeResponse> {
  const result = await callFn<VerifyCodeResponse>("bar-verify-code", {
    email_or_phone: emailOrPhone,
    code,
  });
  if (result.token) setStoredToken(result.token);
  return result;
}

/** Identify the current session. Returns null if the token is bad/expired/revoked.
 * Offline resilient: falls back to cached local profile during network/server drops. */
export async function whoami(): Promise<SessionUser | null> {
  const token = getStoredToken();
  if (!token) return null;
  try {
    const user = await callFn<SessionUser>("bar-whoami");
    try {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    } catch {}
    return user;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      // Bad token or access revoked — clear local state
      setStoredToken(null);
      try {
        localStorage.removeItem(USER_CACHE_KEY);
      } catch {}
      return null;
    }
    // Offline resilience: network failure/server down fallback
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY);
      if (cached) {
        const user = JSON.parse(cached) as SessionUser;
        console.warn("Sentinel OS: Server offline. Using cached user profile.", user);
        return user;
      }
    } catch {}
    throw err;
  }
}

/** Last verified user profile (written by whoami); null if none cached. */
export function getCachedUser(): SessionUser | null {
  try {
    const cached = localStorage.getItem(USER_CACHE_KEY);
    return cached ? (JSON.parse(cached) as SessionUser) : null;
  } catch {
    return null;
  }
}

/** Slide the session's expiry forward. Fire-and-forget on app boot. */
export async function touchSession(): Promise<void> {
  if (!getStoredToken()) return;
  try {
    await callFn("bar-touch-session");
  } catch {
    /* non-fatal — token will simply expire normally */
  }
}

/** Tablet bootstrap: paste a pre-issued long-lived token into localStorage. */
export function setStationToken(token: string): void {
  setStoredToken(token.trim());
}
